import { useEffect, useState, useCallback } from "react";
import { ITenderBid } from "../../types";
import { useAuthHeader } from "react-auth-kit";
import { isAxiosError } from "axios";
import axios, { SESSION_EXPIRED_EVENT } from "../helper/axios";
import { apiURL } from "../helper/urls";

const useTenderBids = () => {
  const [tenderBids, setTenderBids] = useState<ITenderBid[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const getAuthHeader = useAuthHeader();
  const rawHeader = getAuthHeader();
  const token = typeof rawHeader === "string" && rawHeader ? rawHeader.replace(/^Bearer\s+/i, "") : null;

  useEffect(() => {
    const handler = () => setSessionExpired(true);
    window.addEventListener(SESSION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
  }, []);

  const fetchTenderBids = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(
        apiURL("get_bid_list_scaffolding"),
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setTenderBids(data.bids || []);
    } catch (err: unknown) {
      if (isAxiosError(err) && err.response?.status === 401) {
        setSessionExpired(true);
        setLoading(false);
        return;
      }
      const detail =
        isAxiosError(err) && typeof err.response?.data?.detail === "string" ? err.response.data.detail : null;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to fetch tenders"));
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchTenderBids();
    }
  }, [token, fetchTenderBids]);

  const clearSessionExpired = useCallback(() => setSessionExpired(false), []);

  return { tenderBids, loading, error, sessionExpired, clearSessionExpired, fetchTenderBids };
};

export default useTenderBids;
